import { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import { Folder, FileText, Calendar, User, Download, Eye, Search, Edit, RefreshCw } from 'lucide-react';
import DocumentEditModal from '@/components/DocumentEditModal';
import FolderEditModal from '@/components/FolderEditModal';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function ViewDocuments() {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [groupedDocuments, setGroupedDocuments] = useState({});
  const [editingDocument, setEditingDocument] = useState(null);
  const [editingFolder, setEditingFolder] = useState(null);

  useEffect(() => {
    fetchDocuments();
  }, []);

  useEffect(() => {
    groupDocumentsByFolder();
  }, [documents, searchTerm]);

  const fetchDocuments = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/documents`);
      setDocuments(response.data);
    } catch (error) {
      toast.error('Failed to fetch documents');
    } finally {
      setLoading(false);
    }
  };

  const groupDocumentsByFolder = () => {
    const filtered = documents.filter(doc => 
      doc.document_id.toLowerCase().includes(searchTerm.toLowerCase()) ||
      doc.folder_name.toLowerCase().includes(searchTerm.toLowerCase())
    );

    const grouped = filtered.reduce((acc, doc) => {
      const folder = doc.folder_name || 'Uncategorized';
      if (!acc[folder]) {
        acc[folder] = [];
      }
      acc[folder].push(doc);
      return acc;
    }, {});

    setGroupedDocuments(grouped);
  };

  const handleDownload = (verificationId) => {
    const url = `${API}/documents/${verificationId}/pdf`;
    window.open(url, '_blank');
  };

  const handleView = (verificationId) => {
    window.open(`/verify/${verificationId}`, '_blank');
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'Valid':
        return 'bg-green-100 text-green-800 border-green-200';
      case 'Revoked':
        return 'bg-red-100 text-red-800 border-red-200';
      case 'Invalid':
        return 'bg-amber-100 text-amber-800 border-amber-200';
      default:
        return 'bg-slate-100 text-slate-800 border-slate-200';
    }
  };

  if (loading) {
    return (
      <div className="bg-white rounded-lg border border-slate-200 p-8 sm:p-12 text-center">
        <div className="w-10 h-10 sm:w-12 sm:h-12 border-4 border-slate-900 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
        <p className="text-slate-600">Loading documents...</p>
      </div>
    );
  }

  return (
    <div className="space-y-4 sm:space-y-6">
      {/* Header */}
      <div className="bg-white rounded-lg border border-slate-200 p-4 sm:p-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-4 mb-4">
          <div>
            <h2 className="text-xl sm:text-2xl font-bold text-slate-900">All Documents</h2>
            <p className="text-sm sm:text-base text-slate-600 mt-1">
              {documents.length} document{documents.length !== 1 ? 's' : ''} across {Object.keys(groupedDocuments).length} folder{Object.keys(groupedDocuments).length !== 1 ? 's' : ''}
            </p>
          </div>
          <Button
            onClick={fetchDocuments}
            variant="outline"
            size="sm"
            className="w-full sm:w-auto"
            data-testid="refresh-documents-button"
          >
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
        </div>

        {/* Search */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
          <Input
            placeholder="Search by Document ID or Folder..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10 h-10 sm:h-11"
            data-testid="search-documents-input"
          />
        </div>
      </div>

      {/* Documents by Folder */}
      {Object.keys(groupedDocuments).length === 0 ? (
        <div className="bg-white rounded-lg border border-slate-200 p-8 sm:p-12 text-center">
          <FileText className="w-10 h-10 sm:w-12 sm:h-12 text-slate-400 mx-auto mb-4" />
          <p className="text-slate-600">No documents found</p>
        </div>
      ) : (
        <Accordion type="multiple" className="space-y-3 sm:space-y-4" defaultValue={Object.keys(groupedDocuments)}>
          {Object.entries(groupedDocuments).map(([folderName, docs]) => (
            <AccordionItem
              key={folderName}
              value={folderName}
              className="bg-white rounded-lg border border-slate-200 overflow-hidden"
            >
              <AccordionTrigger className="px-4 sm:px-6 py-3 sm:py-4 hover:bg-slate-50 transition-colors" data-testid={`folder-${folderName}`}>
                <div className="flex items-center justify-between w-full pr-2 sm:pr-4">
                  <div className="flex items-center gap-2 sm:gap-3 min-w-0">
                    <div className="w-9 h-9 sm:w-10 sm:h-10 bg-amber-100 rounded-lg flex items-center justify-center flex-shrink-0">
                      <Folder className="w-4 h-4 sm:w-5 sm:h-5 text-amber-700" />
                    </div>
                    <div className="text-left min-w-0">
                      <h3 className="font-semibold text-slate-900 text-sm sm:text-base truncate">{folderName}</h3>
                      <p className="text-xs sm:text-sm text-slate-500">{docs.length} doc{docs.length !== 1 ? 's' : ''}</p>
                    </div>
                  </div>
                  {isAdmin && (
                    <div
                      role="button"
                      tabIndex={0}
                      onClick={(e) => {
                        e.stopPropagation();
                        e.preventDefault();
                        setEditingFolder(folderName);
                      }}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.stopPropagation();
                          e.preventDefault();
                          setEditingFolder(folderName);
                        }
                      }}
                      className="mr-1 sm:mr-2 inline-flex items-center justify-center h-8 w-8 sm:h-9 sm:w-9 rounded-md border border-slate-200 bg-white hover:bg-slate-100 cursor-pointer transition-colors flex-shrink-0"
                    >
                      <Edit className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-slate-600" />
                    </div>
                  )}
                </div>
              </AccordionTrigger>
              <AccordionContent className="px-4 sm:px-6 pb-4">
                <div className="space-y-3 mt-3">
                  {docs.map((doc) => (
                    <div
                      key={doc.id}
                      className="border border-slate-200 rounded-lg p-3 sm:p-4"
                      data-testid={`document-${doc.document_id}`}
                    >
                      {/* Mobile Layout */}
                      <div className="sm:hidden space-y-3">
                        <div className="flex items-center justify-between gap-2">
                          <span className="font-mono font-medium text-slate-900 text-sm truncate" data-testid="doc-id">{doc.document_id}</span>
                          <span className={`px-2 py-0.5 text-xs font-medium rounded border flex-shrink-0 ${getStatusColor(doc.validity_status)}`} data-testid="doc-status">
                            {doc.validity_status}
                          </span>
                        </div>
                        
                        <div className="flex flex-wrap items-center gap-3 text-xs text-slate-600">
                          <div className="flex items-center gap-1">
                            <Calendar className="w-3.5 h-3.5" />
                            <span>{new Date(doc.issue_date).toLocaleDateString()}</span>
                          </div>
                          <div className="flex items-center gap-1">
                            <User className="w-3.5 h-3.5" />
                            <span className="truncate max-w-[100px]">{doc.generated_by}</span>
                          </div>
                        </div>
                        
                        <div className="text-xs text-slate-500 font-mono truncate">
                          {doc.verification_id}
                        </div>
                        
                        <div className="flex gap-2">
                          <Button
                            onClick={() => handleView(doc.verification_id)}
                            variant="outline"
                            size="sm"
                            className="flex-1 h-9 text-xs"
                            data-testid={`view-doc-${doc.document_id}`}
                          >
                            <Eye className="w-3.5 h-3.5 mr-1" />
                            View
                          </Button>
                          <Button
                            onClick={() => handleDownload(doc.verification_id)}
                            variant="outline"
                            size="sm"
                            className="flex-1 h-9 text-xs"
                            data-testid={`download-doc-${doc.document_id}`}
                          >
                            <Download className="w-3.5 h-3.5 mr-1" />
                            Download
                          </Button>
                          {isAdmin && (
                            <Button
                              onClick={() => setEditingDocument(doc)}
                              variant="outline"
                              size="sm"
                              className="h-9 px-2.5"
                              data-testid={`edit-doc-${doc.document_id}`}
                            >
                              <Edit className="w-3.5 h-3.5" />
                            </Button>
                          )}
                        </div>
                      </div>

                      {/* Desktop Layout */}
                      <div className="hidden sm:block">
                        <div className="flex items-start justify-between gap-4">
                          <div className="flex-1 space-y-2 min-w-0">
                            <div className="flex items-center gap-3">
                              <span className="font-mono font-medium text-slate-900" data-testid="doc-id">{doc.document_id}</span>
                              <span className={`px-2 py-1 text-xs font-medium rounded border ${getStatusColor(doc.validity_status)}`} data-testid="doc-status">
                                {doc.validity_status}
                              </span>
                            </div>
                            
                            <div className="flex items-center gap-4 text-sm text-slate-600">
                              <div className="flex items-center gap-1.5">
                                <Calendar className="w-4 h-4" />
                                <span>{new Date(doc.issue_date).toLocaleDateString()}</span>
                              </div>
                              <div className="flex items-center gap-1.5">
                                <User className="w-4 h-4" />
                                <span>{doc.generated_by}</span>
                              </div>
                            </div>
                            
                            <div className="text-xs text-slate-500 font-mono truncate">
                              ID: {doc.verification_id}
                            </div>
                          </div>
                          
                          <div className="flex gap-2 flex-shrink-0">
                            <Button
                              onClick={() => handleView(doc.verification_id)}
                              variant="outline"
                              size="sm"
                              className="flex items-center gap-2"
                              data-testid={`view-doc-${doc.document_id}`}
                            >
                              <Eye className="w-4 h-4" />
                              View
                            </Button>
                            <Button
                              onClick={() => handleDownload(doc.verification_id)}
                              variant="outline"
                              size="sm"
                              className="flex items-center gap-2"
                              data-testid={`download-doc-${doc.document_id}`}
                            >
                              <Download className="w-4 h-4" />
                              Download
                            </Button>
                            {isAdmin && (
                              <Button
                                onClick={() => setEditingDocument(doc)}
                                variant="outline"
                                size="sm"
                                className="flex items-center gap-2"
                                data-testid={`edit-doc-${doc.document_id}`}
                              >
                                <Edit className="w-4 h-4" />
                                Edit
                              </Button>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      )}

      {/* Edit Modals */}
      {editingDocument && (
        <DocumentEditModal
          document={editingDocument}
          open={!!editingDocument}
          onClose={() => setEditingDocument(null)}
          onUpdate={fetchDocuments}
        />
      )}

      {editingFolder && (
        <FolderEditModal
          folderName={editingFolder}
          documents={groupedDocuments[editingFolder] || []}
          open={!!editingFolder}
          onClose={() => setEditingFolder(null)}
          onUpdate={fetchDocuments}
        />
      )}
    </div>
  );
}
